import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from 'core/modules/atoms/Button';
import { Icon } from 'ui';
import Drawer from 'core/modules/components/Drawer/Drawer';
import { getUserIdFromStorage } from '../../../services/serviceConfig';
import type { FlaggedMessageAnnotation, RadarAlert } from '../../../types/radar';
import { ANNOTATION_ITEM_TYPE, RADAR_ALERT_STATUS } from '../../../types/radar.constants';
import { formatFullDate, getInitials } from '../../../utils/formatUtils';
import './_alert-detail-drawer.scss';
import GroupLearningBlockContent from './GroupLearningBlockContent/GroupLearningBlockContent';
import InappropriateUseContent from './InappropriateUseContent/InappropriateUseContent';
import LearningBlockContent from './LearningBlockContent/LearningBlockContent';
import useAlertDetailDrawer from './useAlertDetailDrawer';

export type DrawerNavigation = {
  currentIndex: number;
  onNext: () => void;
  onPrevious: () => void;
  totalCount: number;
};

type AlertDetailDrawerProps = {
  alert: RadarAlert | null;
  isMockMode?: boolean;
  navigation?: DrawerNavigation;
  onClose: () => void;
  onStatusChanged?: (annotationId: string, newStatus: string, reviewerName: string) => void;
  open: boolean;
};

function AlertDetailDrawer({ alert, isMockMode, navigation, onClose, onStatusChanged, open }: AlertDetailDrawerProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const { handleDismiss, handleMarkAsAttended, updating } = useAlertDetailDrawer({ alert, onStatusChanged });

  function handleViewFullAnalysis(): void {
    if (!alert?.kcId || !alert.studentExternalId) return;
    onClose();
    history.push(`/radar/students/${encodeURIComponent(alert.studentExternalId)}/kcs/${encodeURIComponent(alert.kcId)}`);
  }

  function handleStudentClick(): void {
    if (!alert || alert.type === 'group-learning-alert' || !alert.studentExternalId) return;
    onClose();
    history.push(`/radar/students/${encodeURIComponent(alert.studentExternalId)}`);
  }

  const isAtStart = navigation ? navigation.currentIndex <= 0 : true;
  const isAtEnd = navigation ? navigation.currentIndex >= navigation.totalCount - 1 : true;

  function renderHeader(): JSX.Element | null {
    if (!alert) return null;

    return (
      <div className="alert-detail-drawer__header">
        {navigation && (
          <div className="alert-detail-drawer__navigation">
            <div className="alert-detail-drawer__navigation-controls">
              <button
                aria-label={t('chatbot:Previous')}
                className="alert-detail-drawer__navigation-button"
                disabled={isAtStart}
                onClick={navigation.onPrevious}
                type="button"
              >
                <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                </svg>
              </button>
              <span className="alert-detail-drawer__navigation-counter">
                {navigation.currentIndex + 1}/{navigation.totalCount}
              </span>
              <button
                aria-label={t('chatbot:Next')}
                className="alert-detail-drawer__navigation-button"
                disabled={isAtEnd}
                onClick={navigation.onNext}
                type="button"
              >
                <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                </svg>
              </button>
            </div>
            <button aria-label={t('chatbot:Close')} className="alert-detail-drawer__close-button" onClick={onClose} type="button">
              <Icon name="close" size="md" color="grey-03" />
            </button>
          </div>
        )}
        <div className="alert-detail-drawer__student-info">
          {alert.type === 'group-learning-alert' ? (
            <>
              <div className="alert-detail-drawer__avatar alert-detail-drawer__avatar--group">
                <Icon name="students" size="md" color="white" />
              </div>
              <span className="alert-detail-drawer__student-name">{alert.groupName || t('chatbot:radar_group_label')}</span>
            </>
          ) : (
            <>
              <button
                aria-label={t('chatbot:radar_open_student_profile', { name: `${alert.studentName ?? ''} ${alert.studentLastName ?? ''}`.trim() })}
                className="alert-detail-drawer__student-trigger"
                onClick={handleStudentClick}
                type="button"
              >
                <span className="alert-detail-drawer__avatar">
                  {alert.avatar ? (
                    <img alt={`${alert.studentName} ${alert.studentLastName}`} className="alert-detail-drawer__avatar-img" src={alert.avatar} />
                  ) : (
                    getInitials(alert.studentName, alert.studentLastName)
                  )}
                </span>
                <span className="alert-detail-drawer__student-name">
                  {alert.studentName} {alert.studentLastName}
                </span>
              </button>
              {alert.groupName && <span className="alert-detail-drawer__group-name">{alert.groupName}</span>}
            </>
          )}
          <span className="alert-detail-drawer__date">{formatFullDate(alert.createdAt)}</span>
          {!navigation && (
            <button aria-label={t('chatbot:Close')} className="alert-detail-drawer__close-button" onClick={onClose} type="button">
              <Icon name="close" size="md" />
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderFullAnalysisLink(): JSX.Element | null {
    if (!alert || alert.type !== 'learning-block' || !alert.kcId) return null;
    return (
      <Button
        color="transparent-primary"
        onClick={handleViewFullAnalysis}
        size="small"
        text={t('chatbot:radar_view_full_analysis')}
        type="button"
        icon="arrow-back"
      >
        <Icon name="arrow-forward" size="md" color="primary" />
      </Button>
    );
  }

  function renderFooter(): JSX.Element | null {
    if (!alert) return null;

    if (alert.status === RADAR_ALERT_STATUS.PENDING) {
      return (
        <div className="alert-detail-drawer__footer">
          {renderFullAnalysisLink()}
          <div className="alert-detail-drawer__footer-actions">
            <Button
              color="primary-outlined"
              disabled={updating}
              onClick={handleDismiss}
              size="default"
              text={t('chatbot:ai_analytics_student_conversation_dismiss')}
              type="button"
              variant="outline"
            />
            <Button
              color="primary"
              disabled={updating}
              onClick={handleMarkAsAttended}
              size="default"
              text={t('chatbot:ai_analytics_student_conversation_mark_attended')}
              type="button"
            />
          </div>
        </div>
      );
    }

    const isAttended = alert.status === RADAR_ALERT_STATUS.REVIEWED;
    const isResolved = alert.status === RADAR_ALERT_STATUS.RESOLVED;
    const currentUserId = getUserIdFromStorage();
    const isReviewedByMe = alert.reviewedById && alert.reviewedById === currentUserId;
    const reviewerDisplayName = isReviewedByMe ? t('chatbot:radar_reviewed_by_you') : (alert.reviewedByName ?? '');

    let infoText: string;
    let badgeText: string;
    let badgeModifier: string;

    if (isResolved) {
      infoText = t('chatbot:ai_analytics_student_conversation_alert_resolved_info');
      badgeText = t('chatbot:ai_analytics_student_conversation_alert_resolved_badge');
      badgeModifier = 'attended';
    } else if (isAttended) {
      infoText = t('chatbot:ai_analytics_student_conversation_alert_attended_by');
      badgeText = t('chatbot:ai_analytics_inappropriate_use_status_attended');
      badgeModifier = 'attended';
    } else {
      infoText = t('chatbot:ai_analytics_student_conversation_alert_dismissed_by');
      badgeText = t('chatbot:ai_analytics_inappropriate_use_status_dismissed');
      badgeModifier = 'dismissed';
    }

    return (
      <div className="alert-detail-drawer__footer alert-detail-drawer__footer--resolved">
        {renderFullAnalysisLink()}
        <p className="alert-detail-drawer__footer-info">
          {infoText} <strong>{isResolved ? t('chatbot:ai_analytics_student_conversation_alert_resolved_by') : reviewerDisplayName}</strong>
        </p>
        <div className={`alert-detail-drawer__footer-badge alert-detail-drawer__footer-badge--${badgeModifier}`}>
          {(isAttended || isResolved) && <Icon name="done" color="white" size="xl" />}
          {badgeText}
        </div>
      </div>
    );
  }

  function renderContent(): JSX.Element | null {
    if (!alert) return null;

    if (alert.type === 'inappropriate-use' && alert.raw.type === ANNOTATION_ITEM_TYPE.FLAGGED_MESSAGE) {
      return <InappropriateUseContent isMockMode={isMockMode} raw={alert.raw as FlaggedMessageAnnotation} />;
    }

    if (alert.type === 'learning-block') {
      return <LearningBlockContent alert={alert} isMockMode={isMockMode} />;
    }

    if (alert.type === 'group-learning-alert') {
      return <GroupLearningBlockContent alert={alert} isMockMode={isMockMode} />;
    }

    return null;
  }

  return (
    <Drawer className="alert-detail-drawer" footer={renderFooter()} header={renderHeader()} onClose={onClose} open={open} width={490}>
      {renderContent()}
    </Drawer>
  );
}

export default AlertDetailDrawer;
